<!--
  ChatChoiceCard — the in-chat "selector card" list of tappable choices; one component
  serves every step, only the option array differs. Each option renders an icon image OR
  an emoji, a title, an optional sub-label, and a chevron. Emits `select` with the chosen key.
-->
<template>
  <div class="selector-card">
    <button
      v-for="opt in options"
      :key="opt.key"
      type="button"
      class="choice-item"
      :class="{ selected: opt.key === selectedKey }"
      @click="$emit('select', opt.key)"
    >
      <img v-if="opt.icon" :src="opt.icon" class="choice-icon" alt="" />
      <span v-else class="choice-emoji">{{ opt.emoji }}</span>
      <span class="choice-content">
        <span class="choice-title">{{ opt.title }}</span>
        <span v-if="opt.sub" class="choice-sub">{{ opt.sub }}</span>
      </span>
      <span class="choice-arrow" aria-hidden="true">›</span>
    </button>
  </div>
</template>

<script setup lang="ts">
export interface ChoiceOption {
  key: string
  title: string
  sub?: string
  emoji?: string
  icon?: string
}

withDefaults(
  defineProps<{
    options: ChoiceOption[]
    selectedKey?: string | null
  }>(),
  { selectedKey: null },
)

defineEmits<{ select: [key: string] }>()
</script>

<style scoped>
.selector-card {
  width: 92%;
  margin: 0 auto;
  background: var(--white);
  border: 1.5px solid #e2edf5; /* off-palette border — DS pass to arbitrate */
  border-radius: 16px;
  padding: 8px;
  box-shadow: 0 2px 12px rgba(27, 65, 89, 0.06);
  animation: fadeUp 0.3s both;
  display: flex;
  flex-direction: column;
}
.choice-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 14px;
  border: 1.5px solid transparent;
  border-radius: 12px;
  background: transparent;
  cursor: pointer;
  text-align: left;
  font-family: inherit;
  transition: background 0.15s, border-color 0.15s, transform 0.1s;
}
.choice-item:hover {
  background: #f5fafd; /* off-palette hover — DS pass to arbitrate */
}
.choice-item:active {
  transform: scale(0.99);
}
.choice-item.selected {
  border-color: var(--brand-default);
  background: var(--blue-100);
}
.choice-emoji {
  font-size: 24px;
  flex-shrink: 0;
  width: 32px;
  text-align: center;
  line-height: 1;
}
.choice-icon {
  width: 32px;
  height: 32px;
  flex-shrink: 0;
  object-fit: contain;
}
.choice-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 3px;
}
.choice-title {
  font-size: 14px;
  font-weight: 700;
  color: var(--navy-800);
  line-height: 1.3;
}
.choice-sub {
  font-size: 12px;
  color: var(--grey-500);
  line-height: 1.4;
}
.choice-arrow {
  font-size: 20px;
  color: #9bbdd4; /* off-palette chevron — DS pass to arbitrate */
  flex-shrink: 0;
  font-weight: 300;
}
@keyframes fadeUp {
  from {
    opacity: 0;
    transform: translateY(8px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
@media (prefers-reduced-motion: reduce) {
  .selector-card {
    animation: none;
  }
}
</style>
